import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LucideArrowLeft } from 'lucide-react';

/** Shrink-0 so hover background never steals space from the wordmark; gap comes from the parent flex. */
const SIDEBAR_INLINE_TOGGLE_CLASS =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-transparent text-slate-500 transition-colors duration-200 hover:border-slate-200/90 hover:bg-slate-100/80 hover:text-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus-visible:ring-inset focus-visible:ring-offset-0';

/** Shared logo for admin, firm, and company sidebars */
export function SidebarLogo({ collapsed = false }: { collapsed?: boolean }) {
  return (
    <div className="flex items-center">
      {!collapsed && (
        <span className="landing-logo">
          parachute
          <span className="iq">IQ</span>
        </span>
      )}
      {collapsed && (
        <span className="landing-logo leading-none" aria-label="ParachuteIQ" title="ParachuteIQ">
          <span className="iq">IQ</span>
        </span>
      )}
    </div>
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type IconComponent = (props: { size?: number; strokeWidth?: number; className?: string }) => any;

/** Tooltip anchored to the right of the collapsed rail (portal avoids overflow clipping). */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function CollapsedSidebarTooltip({ label, children }: { label: string; children: any }) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const syncPosition = useCallback(() => {
    const el = wrapRef.current;
    if (!el || !(el instanceof HTMLElement)) return;
    const r = el.getBoundingClientRect();
    setPos({ top: r.top + r.height / 2, left: r.right + 8 });
  }, []);

  const show = () => {
    syncPosition();
    setOpen(true);
  };

  const hide = () => setOpen(false);

  useLayoutEffect(() => {
    if (!open) return;
    syncPosition();
  }, [open, syncPosition]);

  useEffect(() => {
    if (!open) return;
    const onHide = () => hide();
    window.addEventListener('scroll', onHide, true);
    window.addEventListener('resize', onHide);
    return () => {
      window.removeEventListener('scroll', onHide, true);
      window.removeEventListener('resize', onHide);
    };
  }, [open]);

  return (
    <>
      <div ref={wrapRef} className="w-full" onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide}>
        {children}
      </div>
      {open &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-[200] -translate-y-1/2 whitespace-nowrap rounded-lg border border-slate-700/80 bg-slate-900 px-3 py-1.5 text-xs font-medium text-white shadow-lg"
            style={{ top: pos.top, left: pos.left }}
          >
            {label}
          </div>,
          document.body
        )}
    </>
  );
}

export interface SidebarItemProps {
  icon: IconComponent;
  label: string;
  active?: boolean;
  onClick: () => void;
  badge?: string | number | null;
  collapsed?: boolean;
  /** Use `danger` for destructive actions (e.g. sign out). */
  variant?: 'default' | 'danger';
}

/** Shared nav item for consistent sidebar styling */
export function SidebarItem({
  icon: Icon,
  label,
  active = false,
  onClick,
  badge,
  collapsed = false,
  variant = 'default',
}: SidebarItemProps) {
  const isDanger = variant === 'danger';

  const stateClasses = active
    ? isDanger
      ? 'bg-red-50 text-red-900 shadow-sm ring-1 ring-red-200/80'
      : 'bg-white text-emerald-900 shadow-sm ring-1 ring-slate-200/80'
    : isDanger
      ? 'text-red-700 hover:bg-red-50 hover:text-red-800'
      : 'text-slate-600 hover:bg-white/70 hover:text-slate-900';

  const iconWrapClasses = active
    ? isDanger
      ? 'text-red-700'
      : 'text-emerald-700'
    : isDanger
      ? 'text-red-600 group-hover:text-red-700'
      : 'text-slate-500 group-hover:text-slate-700';

  const focusRing = isDanger ? 'focus-visible:ring-red-500/40' : 'focus-visible:ring-emerald-500/35';

  const button = (
    <button
      type="button"
      onClick={onClick}
      title={collapsed ? label : undefined}
      aria-label={collapsed ? label : undefined}
      className={`group w-full flex items-center ${
        collapsed ? 'justify-center' : 'justify-between'
      } rounded-xl text-left transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 ${focusRing} focus-visible:ring-offset-2 focus-visible:ring-offset-slate-50 ${
        collapsed ? 'p-3' : 'px-3.5 py-3'
      } ${stateClasses}`}
    >
      <div className={`flex min-w-0 items-center ${collapsed ? '' : 'gap-3.5'}`}>
        <span className={`flex shrink-0 items-center justify-center rounded-lg transition-colors ${iconWrapClasses}`}>
          <Icon size={18} strokeWidth={active ? 2.25 : 1.75} />
        </span>
        {!collapsed && (
          <span className={`truncate text-sm leading-snug font-medium ${active ? 'tracking-tight' : ''}`}>{label}</span>
        )}
      </div>
      {!collapsed && badge != null && badge !== '' && (
        <span className="shrink-0 rounded-full bg-emerald-100/90 px-2 py-0.5 text-[11px] font-medium text-emerald-800">
          {badge}
        </span>
      )}
    </button>
  );

  if (collapsed) {
    return <CollapsedSidebarTooltip label={label}>{button}</CollapsedSidebarTooltip>;
  }

  return button;
}

/** Top shell row: logo column aligned with `AppPageHeader` (white cell; same firm + client portals). Nav uses `AppSidebar` with `omitLogoHeader`. */
export function FirmSidebarLogoHeader({
  collapsed,
  onToggleCollapsed,
}: {
  collapsed: boolean;
  onToggleCollapsed?: () => void;
}) {
  const toggleLabel = collapsed ? 'Expand sidebar' : 'Collapse sidebar';
  const cellClass = `relative flex h-full min-h-[var(--app-shell-header-height)] w-full shrink-0 items-center border-r border-slate-200/80 bg-white py-1.5 ${collapsed ? 'justify-center px-0' : 'justify-start px-4'}`;

  return (
    <div className={`relative flex h-full min-h-0 flex-shrink-0 flex-col ${collapsed ? 'w-16' : 'w-[272px]'}`}>
      {collapsed && onToggleCollapsed ? (
        <button
          type="button"
          onClick={onToggleCollapsed}
          title="Expand sidebar"
          aria-label="Expand sidebar"
          className="absolute left-full top-6 z-30 -ml-px flex h-9 w-7 shrink-0 items-center justify-center rounded-r-lg border border-slate-200/90 border-l-0 bg-white text-slate-500 shadow-sm transition-colors duration-200 hover:border-emerald-200/90 hover:bg-emerald-50/90 hover:text-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
        >
          <LucideArrowLeft size={17} strokeWidth={2} className="rotate-180" aria-hidden />
        </button>
      ) : null}
      {onToggleCollapsed ? (
        <div className={cellClass}>
          {collapsed ? (
            <div className="flex w-full min-w-0 items-center justify-center">
              <SidebarLogo collapsed />
            </div>
          ) : (
            <div className="flex w-full min-w-0 items-center justify-between gap-3">
              <div className="shrink-0">
                <SidebarLogo collapsed={false} />
              </div>
              <button
                type="button"
                onClick={onToggleCollapsed}
                className={SIDEBAR_INLINE_TOGGLE_CLASS}
                title={toggleLabel}
                aria-label={toggleLabel}
              >
                <LucideArrowLeft size={17} strokeWidth={2} aria-hidden />
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className={cellClass}>
          <SidebarLogo collapsed={collapsed} />
        </div>
      )}
    </div>
  );
}

export interface AppSidebarProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  children: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  footer?: any;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** When true (firm layout), logo lives in the shell top row next to the page header — do not render the logo row here. */
  omitLogoHeader?: boolean;
}

/** Shared left sidebar shell: logo, nav (children), optional footer. Used by admin, firm, and company app. */
export default function AppSidebar({
  children,
  footer,
  collapsed = false,
  onToggleCollapsed,
  omitLogoHeader = false,
}: AppSidebarProps) {
  const headerRowClass = `relative flex min-h-[var(--app-shell-header-height)] shrink-0 items-center border-b border-slate-200/70 bg-slate-50/80 py-1.5 shadow-[inset_0_-1px_0_0_rgba(15,23,42,0.04)] backdrop-blur-sm ${collapsed ? 'justify-center px-0' : 'justify-start px-4'}`;
  const toggleLabel = collapsed ? 'Expand sidebar' : 'Collapse sidebar';

  return (
    <div className={`relative flex h-full min-h-0 flex-shrink-0 flex-col ${collapsed ? 'w-16' : 'w-[272px]'}`}>
      {!omitLogoHeader && collapsed && onToggleCollapsed ? (
        <button
          type="button"
          onClick={onToggleCollapsed}
          title="Expand sidebar"
          aria-label="Expand sidebar"
          className="absolute left-full top-6 z-30 -ml-px flex h-9 w-7 shrink-0 items-center justify-center rounded-r-lg border border-slate-200/90 border-l-0 bg-white text-slate-500 shadow-sm transition-colors duration-200 hover:border-emerald-200/90 hover:bg-emerald-50/90 hover:text-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-white"
        >
          <LucideArrowLeft size={17} strokeWidth={2} className="rotate-180" aria-hidden />
        </button>
      ) : null}
      <aside className="flex min-h-0 w-full flex-1 flex-col border-r border-slate-200/80 bg-slate-50 transition-[border-color] duration-200">
        {!omitLogoHeader && onToggleCollapsed ? (
          <div className={`${headerRowClass} w-full min-w-0`}>
            {collapsed ? (
              <div className="flex w-full min-w-0 items-center justify-center">
                <SidebarLogo collapsed />
              </div>
            ) : (
              <div className="flex w-full min-w-0 items-center justify-between gap-3">
                <div className="shrink-0">
                  <SidebarLogo collapsed={false} />
                </div>
                <button
                  type="button"
                  onClick={onToggleCollapsed}
                  className={SIDEBAR_INLINE_TOGGLE_CLASS}
                  title={toggleLabel}
                  aria-label={toggleLabel}
                >
                  <LucideArrowLeft size={17} strokeWidth={2} aria-hidden />
                </button>
              </div>
            )}
          </div>
        ) : !omitLogoHeader ? (
          <div className={headerRowClass}>
            <SidebarLogo collapsed={collapsed} />
          </div>
        ) : null}
        <nav className={`flex flex-1 flex-col gap-1.5 overflow-y-auto py-5 ${collapsed ? 'px-2' : 'px-4'}`}>
          {children}
        </nav>
        {footer != null && (
          <div
            className={`space-y-1.5 border-t border-slate-200/70 bg-slate-100/40 py-5 ${collapsed ? 'px-2' : 'px-4'}`}
          >
            {footer}
          </div>
        )}
      </aside>
    </div>
  );
}
